<template>
  <LSection>
    <template #body>
      <nav
        :key="showMenu"
        class="menuBar pt-6 h-20 text-gray-100 font-light text-sm dark:bg-gray-900"
      >
        <div class="flex justify-between mt-3 h6:mt-0 relative items-center z-10">
          <a href="/" class="flex">
            <img src="../../public/logo.svg" class="h-10 h6:mt-0 h6:h-16" />
          </a>

          <!-- Desktop Navigation -->
          <div class="hidden k1:visible k1:flex k1:gap-4 absolute left-1/2 -translate-x-1/2">
            <router-link to="/about/us" :class="navLinkClass('/about')">About</router-link>
            <router-link to="/accords/about" :class="navLinkClass('/accords')">Accords</router-link>
            <router-link to="/analysis" :class="navLinkClass('/analysis')">Analysis</router-link>
            <div class="relative" @mouseleave="toggleDropdown">
              <button @mouseover="toggleDropdown" class="dropdown-button">Resources</button>
              <div
                v-if="isOpen"
                class="dropdown-content absolute left-0 flex w-44 flex-col items-stretch"
              >
                <router-link
                  v-for="item in resourceLinks"
                  :key="item.to"
                  :to="item.to"
                  class="resource-dropdown-link"
                  :class="navLinkClass(item.path)"
                >
                  {{ item.label }}
                </router-link>
              </div>
            </div>
            
            <router-link to="/news" :class="navLinkClass('/news')">News</router-link>
            <router-link to="/contact" :class="navLinkClass('/contact')">Contact</router-link>
          </div>

          <!-- Right-side Icons & Burger -->
          <div class="ml-8 gap-2 items-center flex k1:gap-4">
            <a href="https://qt.eu" target="_blank" rel="noopener noreferrer">
              <img src="../../public/SVG/logo-qt-contrast.svg" class="h-8 h6:mt-0 h6:h-12 h6:flex hidden" />
            </a>
            <burgerMenu @toggleMenu="showMenu = !showMenu" />
          </div>

          <!-- Mobile Navigation -->
          <div
            v-if="showMenu"
            id="navbar-default"
            class="w-32 text-center k1:hidden bottom-0 right-0 absolute transform translate-y-full k1:w-auto"
            @click="() => (showMenu = false)"
          >
            <div class="flex flex-col p-1 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700 text-base">
              <router-link to="/about/us" :class="mobileNavClass('/about')">About</router-link>
              <router-link to="/accords/about" :class="mobileNavClass('/accords')">Accords</router-link>
              <router-link to="/analysis" :class="mobileNavClass('/analysis')">Analysis</router-link>
              <router-link to="/quantum-strategies-chatbot-tabs" :class="mobileNavClass('/quantum-strategies-chatbot-tabs')">Q.Strategies</router-link>
              <router-link to="/quantum-readiness-chatbot-tabs" :class="mobileNavClass('/quantum-readiness-chatbot-tabs')">Q.Readiness</router-link>
              <router-link to="/quantum-hr-workflow-assistant-tabs" :class="mobileNavClass('/quantum-hr-workflow-assistant-tabs')">Q.HR Workflow</router-link>
              <router-link to="/quantum-career-chatbot-tabs" :class="mobileNavClass('/quantum-career-chatbot-tabs')">Q.Career</router-link>
              <router-link to="/quantum-roadmap-tabs" :class="mobileNavClass('/quantum-roadmap-tabs')">Q.Roadmap</router-link>
              <router-link to="/eqrc-dashboard-tabs" :class="mobileNavClass('/eqrc-dashboard-tabs')">Q.Dashboard</router-link>
              <router-link to="/education&training/europe-qt-community" :class="mobileNavClass('/education&training')">Education</router-link>
              <router-link to="/playlist/page/1" :class="mobileNavClass('/playlist')">Playlist</router-link>
              <router-link to="/impact" :class="mobileNavClass('/impact')">Q.Wall</router-link>
              <router-link to="/news" :class="mobileNavClass('/news')">News</router-link>
              <router-link to="/contact" :class="mobileNavClass('/contact')">Contact</router-link>

              <router-link
                v-if="user?.__isAppAdmin"
                @click="$router.push({ query: { modal: 'ManageAppUsers' } })"
                to="manageAppUsers"
                :class="mobileNavClassByRouteName('manageAppUsers')"
              >
                Manage Users
              </router-link>

              <div
                v-if="account && checkScreenWidth"
                @click="onLogout"
                :class="mobileNavClass()"
              >
                Logout
              </div>
            </div>
          </div>
        </div>
      </nav>
    </template>
  </LSection>
</template>


<script>
import LSection from "@/components/layout/LSection.vue";
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { LOGOUT } from "@/store/operations";
import { capitalize } from "@/lib/typeHelpers";
import { useRoute } from "vue-router";
import BurgerMenu from "@/components/BurgerMenu.vue";
import { useUserPermissions } from "@/composables/useUserPermissions";

export default {
  name: "MenuBar",
  components: {
    LSection,
    BurgerMenu,
  },
  setup() {
    const route = useRoute();
    const store = useStore();
    const showMenu = ref(false);
    const isOpen = ref(false);
    const { isLoggedIn, account, user } = useUserPermissions();

    const onLogout = () => {
      store.dispatch(LOGOUT);
    };

    const checkScreenWidth = () => window.innerWidth < 1000;

    const closeMenu = () => {
      showMenu.value = false;
    };

    const splitName = computed(() =>
      capitalize(account?.value?.name?.split(" ")[0] ?? "")
    );

    const onClickOutside = (event) => {
      const menuElement = event.target.closest(".menuBar");
      if (!menuElement) closeMenu();
    };

    onMounted(() => {
      document.addEventListener("click", onClickOutside);
    });

    onBeforeUnmount(() => {
      document.removeEventListener("click", onClickOutside);
    });

    const navLinkClass = (rootPath) => {
      return {
        'underline decoration-[0.15rem] motion-safe:transition-all motion-safe:duration-200 hover:decoration-[0.25rem]':
          route.path === rootPath || route.path.startsWith(rootPath),
      };
    };

    const mobileNavClass = (rootPath = "") => {
      const isActive =
        route.path === rootPath || (rootPath && route.path.startsWith(rootPath));
      return [
        'block py-2 pr-4 pl-3 text-gray-700 rounded dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white',
        {
          'text-white bg-blue-700 hover:bg-gray-600': isActive,
          'hover:bg-gray-100': !isActive,
        },
      ];
    };

    const mobileNavClassByRouteName = (routeName) => {
      return [
        'block py-2 pr-4 pl-3 text-gray-700 rounded dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white',
        {
          'text-white bg-blue-700 hover:bg-gray-600': route.name === routeName,
          'hover:bg-gray-100': route.name !== routeName,
        },
      ];
    };

    const toggleDropdown = () => {
      isOpen.value = !isOpen.value;
    };

    const resourceLinks = [
      {
        to: "/quantum-strategies-chatbot-tabs",
        path: "/quantum-strategies-chatbot-tabs",
        label: "Q.Strategies",
      },
      {
        to: "/quantum-readiness-chatbot-tabs",
        path: "/quantum-readiness-chatbot-tabs",
        label: "Q.Readiness",
      },
      {
        to: "/quantum-hr-workflow-assistant-tabs",
        path: "/quantum-hr-workflow-assistant-tabs",
        label: "Q.HR Workflow",
      },
      {
        to: "/quantum-career-chatbot-tabs",
        path: "/quantum-career-chatbot-tabs",
        label: "Q.Career",
      },
      {
        to: "/quantum-roadmap-tabs",
        path: "/quantum-roadmap-tabs",
        label: "Q.Roadmap",
      },
      {
        to: "/eqrc-dashboard-tabs",
        path: "/eqrc-dashboard-tabs",
        label: "Q.Dashboard",
      },
      {
        to: "/education&training/europe-qt-community",
        path: "/education&training",
        label: "Education",
      },
      { to: "/playlist/page/1", path: "/playlist", label: "Playlist" },
      { to: "/impact", path: "/impact", label: "Q.Wall" },
    ];

    return {
      isLoggedIn,
      showMenu,
      isOpen,
      account,
      user,
      onLogout,
      checkScreenWidth,
      splitName,
      navLinkClass,
      mobileNavClass,
      mobileNavClassByRouteName,
      toggleDropdown,
      resourceLinks,
    };
  },
};
</script>


<style scoped>
.resource-dropdown-link {
  @apply flex h-10 w-full items-center justify-center rounded bg-gray-50 p-2.5 text-center text-black shadow-md;
  @apply mt-1 mb-1;
}
</style>
